import { Log } from '@/logging/datadog';
import { getOr } from '@/utils/options';
import { createHash } from 'crypto';
import { NextRouter } from 'next/router';
import React from 'react';

const removeHyphen = (phonenumber: string) => {
  return phonenumber.replace(/-/g, '');
};

type values = {
  AuthorizationOnly: string;
  Product_1_Quantity: string;
  Product_1_Discount: string;
  ReferenceNumber: string;
  Currency: string;
  PaymentSuccessfulURLText: string;
  PaymentSuccessfulAutomaticRedirect: string;
  VerificationCode: string;
  MerchantID: string;
};

const getValues = () => {
  const values = {
    AuthorizationOnly: '0',
    Product_1_Quantity: '1',
    Product_1_Discount: '0',
    ReferenceNumber: new Date().getTime().toString(),
    Currency: 'ISK',
    PaymentSuccessfulURLText: 'Halda áfram',
    PaymentSuccessfulAutomaticRedirect: '1',
  };

  return {
    VerificationCode: getOr(process.env.NEXT_PUBLIC_RAPYD_KEY, ''),
    MerchantID: getOr(process.env.NEXT_PUBLIC_RAPYD_MERCHANT_ID, ''),
    ...values,
  };
};

const getSha256Key = (
  values: values,
  price: number,
  pageCallFromURL: string,
  referenceNumber: string,
  phoneNumber: string
) => {
  return createHash('sha256')
    .update(
      values.VerificationCode +
        values.AuthorizationOnly +
        values.Product_1_Quantity +
        price +
        values.Product_1_Discount +
        values.MerchantID +
        values.ReferenceNumber +
        pageCallFromURL +
        `${process.env.NEXT_PUBLIC_VODAFONE_FRELSI_ENDPOINT}/fylla-frelsi?referenceNumber=${referenceNumber}&phoneNumber=${phoneNumber}&price=${price}` +
        values.Currency
    )
    .digest('hex');
};

const startRefill = async (values: values, price: number, id: number, pageCallFromURL: string, phoneNumber: string) => {
  const auth = btoa(process.env.NEXT_PUBLIC_API_USER_NAME + ':' + process.env.NEXT_PUBLIC_API_PASSWORD);
  const api = `${process.env.NEXT_PUBLIC_VODAFONE_EXPERIENCE_ENDPOINT}/mobile/prepaid/${phoneNumber}/refill`;
  try {
    const response = await fetch(api, {
      method: 'POST',
      headers: {
        Authorization: `Basic ${auth}`,
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refill: {
          amount: price,
          productId: id,
        },
        authorizationOnly: '0',
        merchantId: parseInt(values.MerchantID),
        //isKrakkafrelsiProduct: false, //TODO: handle kidsfrelsi (Either call /is-vodafone or add check to available-refills)
        products: {
          quantity: 1, // Always set to 1
          price: price,
          discount: 0,
        },
        paymentSuccessfulURL: pageCallFromURL,
        paymentSuccessfulServerSideURL: `${process.env.NEXT_PUBLIC_VODAFONE_FRELSI_ENDPOINT}/fylla-frelsi`,
        currency: 'ISK',
        //"origin": "OgVodafoneWeb", Not used on DEV
        language: 'is',
      }),
    });
    if (response.status === 200) {
      try {
        const responseMessage = await response.json();
        if (responseMessage.digitalSignatureResponse !== '' && responseMessage.referenceNumber !== '') {
          return responseMessage.referenceNumber;
        }
      } catch (error) {
        Log.error('Error parsing response:', error);
        return;
      }
    }
  } catch (error) {
    Log.error('Error parsing response:', error);
    return;
  }
};

export const sendUserToFrelsiPayment = async (
  router: NextRouter,
  phoneNumber: string | null,
  pageCallFromURL: string,
  packageId: number | undefined,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  id: number,
  price: number
) => {
  const baseURL = pageCallFromURL + '/greidsla';
  const values = getValues();
  const referenceNumber = await startRefill(values, price, id, pageCallFromURL, getOr(phoneNumber, ''));

  if (!referenceNumber) {
    throw Error(`Reference number missing for phone number (${phoneNumber})`, {
      cause: { code: 'noReferenceNumber', values: [phoneNumber, id, price] },
    });
  }
  const digitalSignatureResponse = getSha256Key(
    values,
    price,
    pageCallFromURL,
    referenceNumber,
    getOr(phoneNumber, '')
  );

  const additionalParams = {
    //MerchantID: values.MerchantID,
    packageId: packageId?.toString() ? packageId.toString() : '',
    //Currency: values.Currency,
    ReferenceNumber: values.ReferenceNumber,
    LocalRef: referenceNumber,
    //AuthorizationOnly: values.AuthorizationOnly,
    Product_1_Description: `Áfylling á: ${phoneNumber}`,
    //Product_1_Quantity: values.Product_1_Quantity,
    Product_1_Price: price.toString(),
    //Product_1_Discount: values.Product_1_Discount,
    //PaymentSuccessfulURL: pageCallFromURL,
    //PaymentSuccessfulURLText: values.PaymentSuccessfulURLText,
    PaymentSuccessfulAutomaticRedirect: values.PaymentSuccessfulAutomaticRedirect,
    PaymentSuccessfulServerSideURL: `${
      process.env.NEXT_PUBLIC_VODAFONE_FRELSI_ENDPOINT
    }/fylla-frelsi?referenceNumber=${referenceNumber}&phoneNumber=${getOr(phoneNumber, '')}&price=${price}`,
    PaymentCancelledURL: pageCallFromURL,
    DigitalSignature: await digitalSignatureResponse,
  };

  const queryParams = additionalParams ? `?${new URLSearchParams(additionalParams).toString()}` : '';
  const urlWithParams = `${baseURL}${queryParams}`;

  router.push(urlWithParams).catch(() => {
    setIsLoading(false);
  });
};

export type FrelsiResponse =
  | 'success'
  | 'notVodafone'
  | 'notPrepaid'
  | 'noRefillsAvailable'
  | 'invalidPackage'
  | 'noReferenceNumber'
  | 'error';

export const isValidPackage = async (
  phoneNumber: string,
  requestedPackageId: number,
  requestedPrice: number,
  setFrelsiResponse: React.Dispatch<React.SetStateAction<FrelsiResponse | null>>,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  sendUserToStraumurPayment: () => Promise<void>
) => {
  const number = removeHyphen(phoneNumber);
  const auth = btoa(process.env.NEXT_PUBLIC_API_USER_NAME + ':' + process.env.NEXT_PUBLIC_API_PASSWORD);

  const api = `${process.env.NEXT_PUBLIC_VODAFONE_EXPERIENCE_ENDPOINT}/mobile/prepaid/${number}/available-refills`;

  try {
    const response = await fetch(api, {
      method: 'GET',
      headers: {
        Authorization: `Basic ${auth}`,
      },
    });
    //TODO: handle kidsfrelsi (Either call /is-vodafone or add check to available-refills)
    if (response) {
      try {
        const responseMessage = await response.json();
        if (responseMessage.message === 'Not a Sýn phone number') {
          setFrelsiResponse(() => 'notVodafone');
          setIsLoading(false);
        } else if (responseMessage.message === 'Not a prepaid phone number') {
          setFrelsiResponse(() => 'notPrepaid');
          setIsLoading(false);
        } else if (responseMessage.allowedRefills) {
          responseMessage.allowedRefills.forEach(
            (refills: { amount: number; packageId: number; detailedDescription: { credit: number } }) => {
              if (refills.packageId === requestedPackageId) {
                if (refills.detailedDescription?.credit === requestedPrice || refills.amount === requestedPrice) {
                  setFrelsiResponse(() => 'success');
                  sendUserToStraumurPayment().catch((ex) => {
                    const frelsiResponseCode = ex.cause.code;
                    setFrelsiResponse(() => frelsiResponseCode);
                    setIsLoading(false);
                  });
                } else {
                  setFrelsiResponse(() => 'invalidPackage'); //TODO:: Give better error if prices are not the same
                }
              }
            }
          );
        } else {
          setFrelsiResponse(() => 'notPrepaid');
          setIsLoading(false);
        }
      } catch (error) {
        Log.error('Error:', error);
        setFrelsiResponse(() => 'error');
        setIsLoading(false);
      }
    }
  } catch (error) {
    Log.error('Error:', error);
    setFrelsiResponse(() => 'error');
    setIsLoading(false);
  }
};
